import { useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { useTranslation } from '@/lib/i18n';
import {
  Button,
  PageShell,
  Skeleton,
  StatusBadge,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui';
import { useUserDetailQuery } from '../hooks/useUserDetailQuery';
import { memberStatusLabel } from '../lib/userLabels';
import { UserStatusPanel } from '../components/UserStatusPanel';
import { useMemberGradeDisplay } from '../hooks/useMemberTypeDisplay';
import { UserOrdersPanel } from '../components/UserOrdersPanel';
import { UserCartPanel } from '../components/UserCartPanel';
import { UserCouponPanel } from '../components/UserCouponPanel';
import { UserGradePanel } from '../components/UserGradePanel';
import { UserSummaryCard } from '../components/UserSummaryCard';
import { MemberTypeChangeControl } from '../components/MemberTypeChangeControl';

type TabKey = 'info' | 'orders' | 'cart' | 'coupons' | 'grades';

const BASE_TABS: ReadonlyArray<{ key: TabKey; labelKey: string }> = [
  { key: 'info', labelKey: 'users.tabs.info' },
  { key: 'orders', labelKey: 'users.tabs.orders' },
  { key: 'cart', labelKey: 'users.tabs.cart' },
  { key: 'coupons', labelKey: 'users.tabs.coupons' },
];

// 등급 탭 — 개인회원도 등급(브론즈/실버/골드/VIP)을 갖고 있어 모든 회원에게 연다. 예전 D1 은 개인회원을
// 단일 기본 등급으로 보고 숨겼지만, 등급 체계는 회원 유형과 무관하게 하나이고 BE 등급 변경도 유형을 따지지 않는다.
const GRADE_TAB = { key: 'grades' as const, labelKey: 'users.tabs.grades' };

const STATUS_VARIANT = {
  ACTIVE: 'success',
  DORMANT: 'warning',
  SUSPENDED: 'danger',
  WITHDRAWN: 'neutral',
  // R6 O — 기업회원 승인 게이트 상태.
  PENDING_APPROVAL: 'warning',
  REJECTED: 'danger',
} as const;

export const UserDetailPage = () => {
  const { t } = useTranslation('common');
  const { userId } = useParams<{ userId: string }>();
  const [activeTab, setActiveTab] = useState<TabKey>('info');

  const query = useUserDetailQuery(userId ?? '');

  const breadcrumb = useMemo(
    () => [
      { label: t('sidebar.groups.users', '회원') },
      { label: t('users.list.title', '회원 관리'), href: '/users/members' },
      { label: query.data?.name ?? t('users.detail.loading', '회원 정보 불러오는 중...') },
    ],
    [t, query.data?.name],
  );
  // QA-414 — 사업자는 현재 등급까지(예: 사업자(1등급)). 훅이라 아래 조기 반환보다 먼저 부른다.
  // 상단 요약은 회원 유형 대신 등급(회원 유형은 아래 [회원 유형] 카드에 그대로).
  const memberTypeDisplay = useMemberGradeDisplay(query.data?.memberType, query.data?.gradeId, (query.data as { gradeName?: string | null } | undefined)?.gradeName);

  if (query.isPending) {
    return (
      <PageShell
        title={t('users.detail.loading')}
        backHref="/users/members"
        backLabel={t('users.detail.backToList')}
      >
        <div className="space-y-3" role="status" aria-live="polite">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-[44px] w-full" />
          ))}
        </div>
      </PageShell>
    );
  }

  if (query.isError || !query.data) {
    return (
      <PageShell
        title={t('users.detail.error')}
        backHref="/users/members"
        backLabel={t('users.detail.backToList')}
      >
        <div
          role="alert"
          className="flex flex-col items-center gap-3 rounded-lg py-12 text-center text-sm"
          style={{ background: 'var(--status-danger-bg)', color: 'var(--status-danger-fg)' }}
        >
          <p>{t('users.detail.loadFailed')}</p>
          <Button variant="outline" size="sm" onClick={() => void query.refetch()}>
            {t('users.actions.retry')}
          </Button>
        </div>
      </PageShell>
    );
  }

  const user = query.data;

  const tabs = [...BASE_TABS, GRADE_TAB];
  const safeActiveTab: TabKey = activeTab;

  return (
    <PageShell
      title={user.name}
      breadcrumb={breadcrumb}
      backHref="/users/members"
      backLabel={t('users.detail.backToList')}
      data-testid="user-detail-page"
    >
      <div className="flex flex-wrap items-center gap-2 text-[12px]" style={{ color: 'var(--content-muted)' }}>
        <span>{user.email}</span>
        <span>·</span>
        <span data-testid="member-type-summary">{memberTypeDisplay}</span>
        <span>·</span>
        <StatusBadge label={memberStatusLabel(t, user.status, user.memberType)} variant={STATUS_VARIANT[user.status] ?? 'neutral'} size="sm" />
        {user.locked ? (
          <StatusBadge label={t('users.locked.badge')} variant="danger" size="sm" />
        ) : null}
      </div>

      <UserSummaryCard user={user} />

      <div className="mb-4">
        <MemberTypeChangeControl user={user} />
      </div>

      <Tabs value={safeActiveTab} onValueChange={(v) => setActiveTab(v as TabKey)}>
        <TabsList>
          {tabs.map((tab) => (
            <TabsTrigger key={tab.key} value={tab.key}>
              {t(tab.labelKey)}
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="info">
          <UserStatusPanel user={user} />
        </TabsContent>
        <TabsContent value="orders">
          <UserOrdersPanel userId={user.userId} />
        </TabsContent>
        <TabsContent value="cart">
          <UserCartPanel userId={user.userId} />
        </TabsContent>
        <TabsContent value="coupons">
          <UserCouponPanel userId={user.userId} />
        </TabsContent>
        <TabsContent value="grades">
          <UserGradePanel userId={user.userId} />
        </TabsContent>
      </Tabs>
    </PageShell>
  );
};
